"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { FulfillmentMode } from "@/lib/fulfillment";
import type { AppSchemasCustomerAddress } from "@/api/online-ordering-service/model";
import { scopedStorageKey } from "@/lib/storageScope";
import { useBranch } from "@/store/branch";

type FulfillmentCtx = {
  mode: FulfillmentMode;
  setMode: (mode: FulfillmentMode) => void;
  selectedAddressId: string | null;
  setSelectedAddressId: (id: string | null) => void;
  manualAddress: AppSchemasCustomerAddress | null;
  setManualAddress: (address: AppSchemasCustomerAddress | null) => void;
};

const FulfillmentContext = createContext<FulfillmentCtx | null>(null);

const STORAGE_KEY = "v2_fulfillment";

type StoredFulfillment = {
  mode?: FulfillmentMode;
  selectedAddressId?: string | null;
  manualAddress?: AppSchemasCustomerAddress | null;
};

function storageKeyFor(slug: string | null | undefined): string {
  return scopedStorageKey(STORAGE_KEY, slug);
}

function readStored(storageKey: string): Required<StoredFulfillment> {
  if (typeof window === "undefined") {
    return { mode: "delivery", selectedAddressId: null, manualAddress: null };
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? (JSON.parse(raw) as StoredFulfillment) : {};
    return {
      mode: parsed.mode === "pickup" ? "pickup" : "delivery",
      selectedAddressId:
        typeof parsed.selectedAddressId === "string"
          ? parsed.selectedAddressId
          : null,
      manualAddress: parsed.manualAddress ?? null,
    };
  } catch {
    return { mode: "delivery", selectedAddressId: null, manualAddress: null };
  }
}

function writeStored(storageKey: string, state: Required<StoredFulfillment>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // storage unavailable
  }
}

export function FulfillmentProvider({ children }: { children: ReactNode }) {
  const { branchSlug } = useBranch();
  const storageKey = storageKeyFor(branchSlug);
  const [state, setState] = useState<Required<StoredFulfillment>>(() =>
    readStored(storageKey),
  );
  const lastKeyRef = useRef(storageKey);
  const skipNextPersistRef = useRef(false);

  useEffect(() => {
    if (lastKeyRef.current !== storageKey) {
      lastKeyRef.current = storageKey;
      skipNextPersistRef.current = true;
      setState(readStored(storageKey));
    }
  }, [storageKey]);

  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    writeStored(storageKey, state);
  }, [state, storageKey]);

  const setMode = useCallback((mode: FulfillmentMode) => {
    setState((prev) => ({ ...prev, mode }));
  }, []);

  const setSelectedAddressId = useCallback((id: string | null) => {
    setState((prev) => ({ ...prev, selectedAddressId: id, manualAddress: null }));
  }, []);

  const setManualAddress = useCallback((address: AppSchemasCustomerAddress | null) => {
    setState((prev) => ({ ...prev, manualAddress: address, selectedAddressId: null }));
  }, []);

  const value = useMemo<FulfillmentCtx>(
    () => ({
      mode: state.mode,
      setMode,
      selectedAddressId: state.selectedAddressId,
      setSelectedAddressId,
      manualAddress: state.manualAddress,
      setManualAddress,
    }),
    [
      setMode,
      setSelectedAddressId,
      setManualAddress,
      state.mode,
      state.selectedAddressId,
      state.manualAddress,
    ],
  );

  return (
    <FulfillmentContext.Provider value={value}>
      {children}
    </FulfillmentContext.Provider>
  );
}

export function useFulfillment(): FulfillmentCtx {
  const ctx = useContext(FulfillmentContext);
  if (!ctx) {
    throw new Error("useFulfillment must be used within a FulfillmentProvider");
  }
  return ctx;
}
